import React, { useEffect, useState } from "react";
import { Button, useToast, VStack } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";
import WaitlistMessagingPreferences from "../components/waitlist/WaitlistMessagingPreferences";

export default function OnboardingWaitlistCustomize() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copy, setCopy] = useState({});
  const [products, setProducts] = useState([]);
  const [enabledProductIds, setEnabledProductIds] = useState(new Set());
  const [daysAway, setDaysAway] = useState(7);
  const [openSlotDaysAway, setOpenSlotDaysAway] = useState(3);
  const [frequencyDays, setFrequencyDays] = useState(7);

  useEffect(() => {
    fetchPreferences();
    markStepViewed();
  }, []);

  const markStepViewed = async () => {
    try {
      window.sessionStorage?.setItem("onboarding.waitlist_viewed", "1");
    } catch (error) {
      // Ignore storage issues and continue.
    }

    try {
      await fetch("/api/onboarding_progress", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ step_id: "waitlist" }),
      });
    } catch (error) {
      // Non-blocking: viewing completion should not interrupt page usage
    } finally {
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
    }
  };

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/waitlist_preferences");
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load waitlist preferences");
      }
      setCopy(data.copy || {});
      setProducts(data.products || []);
      setEnabledProductIds(
        new Set((data.products || []).filter((p) => p.waitlist_text_enabled).map((p) => p.id))
      );
      if (data.preferences) {
        setDaysAway(data.preferences.waitlist_existing_appt_days_threshold ?? 7);
        setOpenSlotDaysAway(data.preferences.waitlist_open_slot_days_threshold ?? 3);
        setFrequencyDays(data.preferences.waitlist_text_frequency_days ?? 7);
      }
    } catch (error) {
      setCopy({});
      setProducts([]);
      setEnabledProductIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = (productId) => {
    setEnabledProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch("/api/waitlist_preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          preferences: {
            waitlist_existing_appt_days_threshold: daysAway,
            waitlist_open_slot_days_threshold: openSlotDaysAway,
            waitlist_text_frequency_days: frequencyDays,
          },
          enabled_product_ids: Array.from(enabledProductIds),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.errors?.join(", ") || "Failed to save preferences");
      }

      toast({
        title: "Waitlist preferences saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
      navigate("/onboarding/availability");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout
      title="Customize Your Waitlist"
      subtitle="Adjust how your waitlist behaves and how clients interact with it."
    >
      <VStack align="stretch" spacing={6}>
        <WaitlistMessagingPreferences
          loading={loading}
          daysAway={daysAway}
          setDaysAway={setDaysAway}
          openSlotDaysAway={openSlotDaysAway}
          setOpenSlotDaysAway={setOpenSlotDaysAway}
          frequencyDays={frequencyDays}
          setFrequencyDays={setFrequencyDays}
          products={products}
          enabledProductIds={enabledProductIds}
          onToggleProduct={handleToggleProduct}
          showMessages
          copy={copy}
        />

        <Button
          colorScheme="blue"
          alignSelf="flex-start"
          onClick={handleSave}
          isLoading={saving}
        >
          Save Texting Preferences
        </Button>

        <Button
          alignSelf="flex-start"
          variant="ghost"
          onClick={() => navigate("/onboarding/waitlist")}
        >
          Back to Create Your Waitlist
        </Button>
      </VStack>
    </OnboardingLayout>
  );
}
